import { useEffect, useState, useMemo } from 'react';
import { VoicePhoneNumber } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-numbers/phone_numbers_service.pb';
import { DialQueries } from '@frontend/api-dialpad';
import { useMutation, useQuery } from '@frontend/react-query-helpers';
import { PhoneNumberSchemaService, SchemaPhoneUserService } from '@frontend/schema';
import { OutboundSelectedPhoneNumberProps } from '..';

export const useOutboundCallerIdNumbers = ({
  id,
  hasPhoneNumbersNameIdFlag,
}: {
  id: string;
  hasPhoneNumbersNameIdFlag: boolean;
}) => {
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<OutboundSelectedPhoneNumberProps | undefined>();

  const { data: numbersWithFeatureFlag, refetch: refetchNumbersWithFlag } = useQuery({
    queryKey: DialQueries.queryKeys.getNumbersBySipProfileId(id),
    queryFn: () => PhoneNumberSchemaService.ListOutboundCallerNumbers({ sipProfileId: id }),
    enabled: !!id && !!hasPhoneNumbersNameIdFlag,
  });

  const { data: numbersWithoutFeatureFlag, refetch: refetchNumbersWithoutFlag } = DialQueries.useVoiceNumbersQuery(id, {
    enabled: !!id,
  });

  const data = hasPhoneNumbersNameIdFlag ? numbersWithFeatureFlag?.outboundCallerNumbers : numbersWithoutFeatureFlag;
  const refetchNumbers = hasPhoneNumbersNameIdFlag ? refetchNumbersWithFlag : refetchNumbersWithoutFlag;

  const { mutate: setPreferredNumber } = useMutation({
    mutationFn: (phoneNumberId: string) => SchemaPhoneUserService.SetPreferredPhoneNumber({ phoneNumberId }),
    onSuccess: () => {
      refetchNumbers();
    },
  });

  const { numbers, initialSelectedPhoneNumber, preferredNumber } = useMemo(() => {
    return processedOutboundNumbers({ data });
  }, [data]);

  useEffect(() => {
    setSelectedPhoneNumber(initialSelectedPhoneNumber);
    /* This is to backfill the backend data, if no number in the numbers array is preferred,
    we want to set the first item in the array with a phoneNumberId  as the
    preferred number
    */
    if (!preferredNumber) {
      const defaultPhoneNum = numbers?.find((num) => !!num?.phoneNumberId);
      if (defaultPhoneNum) {
        setPreferredNumber(defaultPhoneNum.phoneNumberId);
      }
    }
  }, [numbers, setPreferredNumber, preferredNumber, initialSelectedPhoneNumber]);

  return {
    selectedPhoneNumber,
    numbers,
    setSelectedPhoneNumber,
  };
};

export function processedOutboundNumbers({ data }: { data: VoicePhoneNumber[] | undefined }) {
  const numbers =
    data?.flatMap(
      ({ location, phoneNumbers }) =>
        phoneNumbers?.map<OutboundSelectedPhoneNumberProps>((item) => ({
          isPreferred: item.isPreferred ?? false,
          locationName: location?.name ?? '',
          number: item.number?.nationalNumber?.toString() ?? '',
          phoneNumberId: item.id ?? '',
          phoneNumberName: item.displayName ?? '',
        })) ?? []
    ) ?? [];

  const preferredNumber = numbers?.find((num) => num?.isPreferred);
  const initialSelectedPhoneNumber = numbers?.length === 1 ? numbers[0] : preferredNumber;

  return { numbers, initialSelectedPhoneNumber, preferredNumber };
}

export const useNumbersToLocationMap = (outboundNumbers: OutboundSelectedPhoneNumberProps[] | undefined) => {
  const map: Record<string, string> = {};
  outboundNumbers?.forEach((item) => {
    const locationName = item.locationName;
    map[item.number] = locationName;
  });
  return map;
};
